// @ts-strict-ignore
import md5 from 'md5';
import stableStringify from 'json-stable-stringify';

import {apiClient} from 'common/util/api-client';
import type {Bookmark} from 'common/report/dashboards/types';
import {ReportName} from 'common/report/constants';
import {generateBookmarkURLSlug} from 'common/report/util/bookmark-urls';
import {beforeCleanerUrl} from 'common/report/report-manager/query-lifecycles';

import {appApiUrl} from '../utils';

export type BookmarkURLResponse = {
  slug: string;
  created_at: string;
  bookmark?: Bookmark;
  user_id: number;
  project_id: number;
  workspace_id?: number;
  type: ReportName;
  bookmark_id?: number;
  params: Obj;
  name?: string;
  description?: string;
  overrides?: Obj;
};

export type BookmarkURLParams = {
  project_id: number;
  type: ReportName;
  bookmark_id?: number;
  params: Obj;
  name?: string;
  description?: string;
  overrides?: Obj;
};

function buildEndpoint({projectId, slug}: {projectId: number; slug?: string}): string {
  return appApiUrl(`bookmark-urls/${slug ? `${slug}/` : ``}`, {projectId});
}

const previousBookmarkSlugs = new Map<string, string>();

/**
 * Gets URL slug based on bookmark params. Reuses previous slugs if params don't change.
 */
function getSlug(params: BookmarkURLParams): {
  slug: string;
  reused: boolean;
} {
  const paramsHash = md5(stableStringify(params));
  const previousSlug = previousBookmarkSlugs.get(paramsHash);

  if (previousSlug) {
    return {slug: previousSlug, reused: true};
  } else {
    const slug = generateBookmarkURLSlug();
    previousBookmarkSlugs.set(paramsHash, slug);

    return {slug, reused: false};
  }
}

function getHeaders() {
  // TODO: Will need to pass a store into this at some point probably
  return {'x-single-report': `1`};
}

/**
 * Retrieves a bookmark by slug.
 */
async function fetchBookmarkUrl(params: {projectId: number; slug: string}) {
  return apiClient.get<BookmarkURLResponse>(buildEndpoint(params), null, {
    updateFetchCache: true,
    headers: getHeaders(),
  });
}

/**
 * Resets bookmark URL cache so previous slugs won't be reused.
 */
export function resetBookmarkUrlCache() {
  previousBookmarkSlugs.clear();
}

/**
 * Gets (and saves if necessary) a bookmark slug given params.
 */
function getBookmarkUrl(params: BookmarkURLParams): string {
  const {slug, reused} = getSlug(params);

  if (!reused) {
    const requestParams = beforeCleanerUrl(params);

    apiClient.post<BookmarkURLResponse>(
      buildEndpoint({projectId: params[`project_id`]}),
      {
        slug,
        ...requestParams,
      },
      {headers: getHeaders()},
    );
  }

  return slug;
}

// export here for better testing
export const bookmarkUrlsClient = {
  getBookmarkUrl,
  fetchBookmarkUrl,
};
